import React, { useMemo, useState } from 'react';
import BulkSummaryHeader from 'src/components/BulkOperations/_Shared/BulkSummaryHeader/BulkSummaryHeader';
import TabSwitch, { TabSwitchTab } from 'src/components/TabSwitch/TabSwitch';
import { IServiceRequest } from 'src/services/api/ServiceRequestDashboard/apiContracts';
import { BulkUpdateVisibilityResult } from 'src/services/api/ServiceRequestDashboard/useServiceRequestVisibilityApi';
import ConfirmationSection, {
  ConfirmationSectionVariant,
} from 'src/pages/ServiceRequests/components/ServiceRequestVisibilityDialogue/StepVisibilityConfirmation/ConfirmationSection';
import './step-visibility-confirmation.scss';

interface StepVisibilityConfirmationProps {
  result: BulkUpdateVisibilityResult | null;
  changedServiceRequests: IServiceRequest[];
  unchangedServiceRequests: IServiceRequest[];
  targetIsInternalOnly: boolean;
}

type ChangedTabId = 'changed';
type UnchangedTabId = 'unchanged';
type ConfirmationTabId = ChangedTabId | UnchangedTabId;

const TAB_ID_CHANGED: ChangedTabId = 'changed';
const TAB_ID_UNCHANGED: UnchangedTabId = 'unchanged';

const StepVisibilityConfirmation = ({
  result,
  changedServiceRequests,
  unchangedServiceRequests,
  targetIsInternalOnly,
}: StepVisibilityConfirmationProps) => {
  const [activeTabId, setActiveTabId] = useState<ConfirmationTabId>(
    TAB_ID_CHANGED
  );

  const totalUpdated: number = result?.updatedCount ?? 0;

  const headline: string =
    totalUpdated === 1
      ? '1 service request updated'
      : `${totalUpdated} service requests updated`;

  const changedVariant: ConfirmationSectionVariant = targetIsInternalOnly
    ? 'internal'
    : 'external';

  const changedHeading: string = targetIsInternalOnly
    ? 'Now internal'
    : 'Now external';

  const hasUnchanged: boolean = unchangedServiceRequests.length > 0;

  const hasAnyEntries: boolean =
    changedServiceRequests.length > 0 || hasUnchanged;

  const tabs: TabSwitchTab[] = useMemo(() => {
    const built: TabSwitchTab[] = [
      {
        id: TAB_ID_CHANGED,
        label: changedHeading,
        count: changedServiceRequests.length,
      },
    ];

    if (hasUnchanged) {
      built.push({
        id: TAB_ID_UNCHANGED,
        label: 'Unchanged',
        count: unchangedServiceRequests.length,
      });
    }

    return built;
  }, [
    changedHeading,
    changedServiceRequests.length,
    hasUnchanged,
    unchangedServiceRequests.length,
  ]);

  const showTabSwitch: boolean = tabs.length > 1;

  return (
    <div className="svc-step">
      <BulkSummaryHeader
        title={headline}
        subtitle="Visibility settings were saved successfully."
        centered
      />
      {hasAnyEntries && (
        <div className="svc-sections">
          {showTabSwitch && (
            <div className="svc-tab-switch-row">
              <TabSwitch
                tabs={tabs}
                activeTabId={activeTabId}
                onTabChange={(id: string) =>
                  setActiveTabId(id as ConfirmationTabId)
                }
              />
            </div>
          )}
          {activeTabId === TAB_ID_CHANGED && (
            <ConfirmationSection
              variant={changedVariant}
              heading={changedHeading}
              serviceRequests={changedServiceRequests}
            />
          )}
          {activeTabId === TAB_ID_UNCHANGED && (
            <ConfirmationSection
              variant="unchanged"
              heading="Unchanged"
              serviceRequests={unchangedServiceRequests}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default StepVisibilityConfirmation;